import type { RuleTemplate, AggregatedHolding } from "../../types";
import React, { useState } from 'react';
import LoadingSpinner from './LoadingSpinner';

interface RuleTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: RuleTemplate[];
  holdings: AggregatedHolding[];
  onApplyTemplate: (template: RuleTemplate, selectedHoldings: string[], accountScope: 'all' | 'specific', accountId?: number) => Promise<void>;
}

const RuleTemplatesModal: React.FC<RuleTemplatesModalProps> = ({
  isOpen,
  onClose,
  templates,
  holdings,
  onApplyTemplate
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<RuleTemplate | null>(null);
  const [selectedHoldings, setSelectedHoldings] = useState<string[]>([]);
  const [accountScope, setAccountScope] = useState<'all' | 'specific'>('all');
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get all unique accounts across holdings
  const allAccounts = holdings.reduce((acc, holding) => {
    holding.accounts.forEach(account => {
      const existing = acc.find(a => a.broker_account_id === account.broker_account_id);
      if (!existing) {
        acc.push(account);
      }
    });
    return acc;
  }, [] as Array<{broker_account_id: number, broker_name: string, broker_type: string}>);

  const handleSelectHolding = (symbol: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedHoldings(prev => [...prev, symbol]);
    } else {
      setSelectedHoldings(prev => prev.filter(s => s !== symbol));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedHoldings(holdings.map(h => h.symbol));
    } else {
      setSelectedHoldings([]);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate || selectedHoldings.length === 0) return;

    try {
      setIsSubmitting(true);
      await onApplyTemplate(
        selectedTemplate, 
        selectedHoldings, 
        accountScope, 
        accountScope === 'specific' ? selectedAccountId : undefined
      );
      
      // Reset form
      setSelectedTemplate(null);
      setSelectedHoldings([]);
      setAccountScope('all');
      setSelectedAccountId(undefined);
      onClose();
    } catch (error) {
      console.error('Error applying template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getApplicableHoldings = () => {
    if (accountScope === 'all') {
      return holdings;
    }
    
    return holdings.filter(holding => 
      holding.accounts.some(account => account.broker_account_id === selectedAccountId)
    );
  };

  const formatTemplate = (template: RuleTemplate) => {
    // Note: SELL templates are temporarily disabled until broker integration is ready
    let description = `${template.action_type === 'SELL' ? 'Sell' : 'Alert'} when price `;
    description += template.trigger_type === 'TRAILING_STOP_LOSS' 
      ? `drops ${template.trigger_value}% from peak` 
      : `falls below ₹${template.trigger_value}`;
    
    if (template.action_type === 'SELL') {
      if (template.sell_quantity_type === 'ALL_REMAINING') {
        description += ' (Sell all)';
      } else if (template.sell_quantity_type === 'PERCENTAGE') {
        description += ` (Sell ${template.sell_quantity_value}%)`;
      } else {
        description += ` (Sell ${template.sell_quantity_value} shares)`;
      }
    }
    
    return description;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Apply Rule Templates
                </h3>
                <p className="text-sm text-gray-500">
                  Apply predefined rules to multiple holdings at once
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Template Selection */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Select Template</h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedTemplate?.id === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          checked={selectedTemplate?.id === template.id}
                          onChange={() => setSelectedTemplate(template)}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <div className="font-medium text-gray-900">{template.name}</div>
                          <div className="text-sm text-gray-600 mt-1">{template.description}</div>
                          <div className="text-xs text-gray-500 mt-2">{formatTemplate(template)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Holdings and Account Selection */}
              <div>
                <div className="mb-4">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Account Scope</h4>
                  <div className="space-y-3">
                    <label className="flex items-start">
                      <input
                        type="radio"
                        value="all"
                        checked={accountScope === 'all'}
                        onChange={(e) => setAccountScope(e.target.value as 'all')}
                        className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">All Accounts</div>
                        <div className="text-sm text-gray-500">Apply rules to all accounts holding selected stocks</div>
                      </div>
                    </label>

                    <label className="flex items-start">
                      <input
                        type="radio"
                        value="specific"
                        checked={accountScope === 'specific'}
                        onChange={(e) => setAccountScope(e.target.value as 'specific')}
                        className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">Specific Account</div>
                        <div className="text-sm text-gray-500">Apply rules to a specific account only</div>
                      </div>
                    </label>

                    {accountScope === 'specific' && (
                      <div className="ml-7">
                        <select
                          value={selectedAccountId || ''}
                          onChange={(e) => setSelectedAccountId(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Account</option>
                          {allAccounts.map((account) => (
                            <option key={account.broker_account_id} value={account.broker_account_id}>
                              {account.broker_name} ({account.broker_type})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <h4 className="text-md font-medium text-gray-900 mb-4">
                  Select Holdings 
                  {accountScope === 'specific' && selectedAccountId && (
                    <span className="text-sm font-normal text-gray-500">
                      (Filtered by selected account)
                    </span>
                  )}
                </h4>
                
                <div className="mb-3">
                  <label className="flex items-center text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={selectedHoldings.length === getApplicableHoldings().length && getApplicableHoldings().length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    Select All ({getApplicableHoldings().length} holdings)
                  </label>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {getApplicableHoldings().map((holding) => (
                    <label key={holding.symbol} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedHoldings.includes(holding.symbol)}
                        onChange={(e) => handleSelectHolding(holding.symbol, e.target.checked)}
                        className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{holding.symbol}</div>
                        <div className="text-xs text-gray-500">
                          {holding.company_name} • {holding.total_quantity} shares
                          {accountScope === 'specific' && selectedAccountId && (
                            <> • {holding.accounts.find(acc => acc.broker_account_id === selectedAccountId)?.quantity || 0} in selected account</>
                          )}
                        </div>
                        {holding.active_rules_count > 0 && (
                          <div className="text-xs text-orange-600">
                            ⚠️ Already has {holding.active_rules_count} active rule(s)
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary */}
            {selectedTemplate && selectedHoldings.length > 0 && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">Application Summary</h5>
                <div className="text-sm text-blue-800 space-y-1">
                  <div><strong>Template:</strong> {selectedTemplate.name}</div>
                  <div><strong>Holdings:</strong> {selectedHoldings.length} selected</div>
                  <div><strong>Scope:</strong> {accountScope === 'all' ? 'All accounts' : `Specific account only`}</div>
                  {accountScope === 'specific' && selectedAccountId && (
                    <div><strong>Account:</strong> {allAccounts.find(acc => acc.broker_account_id === selectedAccountId)?.broker_name}</div>
                  )}
                  <div className="text-xs text-blue-600 mt-2">
                    This will create {selectedHoldings.length} new rule(s).
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyTemplate}
              disabled={!selectedTemplate || selectedHoldings.length === 0 || (accountScope === 'specific' && !selectedAccountId) || isSubmitting}
              className={`px-6 py-2 rounded-lg transition-colors ${
                selectedTemplate && selectedHoldings.length > 0 && (accountScope === 'all' || selectedAccountId) && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <LoadingSpinner />
              ) : (
                `Apply to ${selectedHoldings.length} Holdings`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleTemplatesModal; 